import { RootState } from 'store/store';
import { useAppDispatch, useAppStore } from 'store/store-hooks';
import { StoryBoard } from 'services/storyboard.service';
import { getSbTracks } from 'components/app/_helpers/_app.helper';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { EMPTY_CELL, handleMarkers } from 'components/app/editor/_mediator/editor.slice';
import { handleSbLinks } from 'components/app/editor/_mediator/editor-clips/editor-clips-sb.helper';
import { setRecentlyMovedClips } from 'components/app/editor/_mediator/editor-controls.slice';
import { getCursorCell, isSmallRegion, markersApi } from './editor-markers.helper';

type Error = 'smallRegion' | 'brokenLinks' | 'notEnoughCells';
type Params = {
  dispatch?: boolean;
  fixLinks?: boolean;
  handleError?: boolean;
  selectedMarkers?: Index[];
  storyBoard?: StoryBoard;
  markers?: MarkerId[];
};

export const useMarkersCb = () => {
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const parseParams = (params?: Params) => {
    const state = selectData(store.getState());
    return {
      ...state,
      selectedMarkers: params?.selectedMarkers ?? state.selectedMarkers,
      storyBoard: params?.storyBoard ?? state.storyBoard,
      markers: params?.markers ?? state.markers,
      isDispatch: params?.dispatch ?? true,
      isFixLinks: params?.fixLinks ?? true,
      isErr: params?.handleError ?? true,
    };
  };

  const handleError = (error: Error) => {
    if (error === 'brokenLinks') Error.handleSbLinks();
    else if (error === 'smallRegion') Error.tooSmallRegion();
    else if (error === 'notEnoughCells') Error.notEnoughCells();
  };

  const handleLinks = (
    res: { storyBoard: StoryBoard; markers: MarkerId[] },
    data: ReturnType<typeof parseParams>,
    replacedClips: ClipId[] = [],
    isDel?: boolean
  ) => {
    const { storyBoard, linkedClips, isDispatch, isErr, isFixLinks } = data;
    if (isFixLinks) {
      const _res = handleSbLinks(linkedClips, res.storyBoard, storyBoard);
      if (_res.status === 'cantBeFixed') {
        if (isErr) handleError('brokenLinks');
        return;
      }
      res.storyBoard = _res.storyBoard as StoryBoard;
      dispatch(setRecentlyMovedClips([..._res.processedClips, ...replacedClips]));
    }
    if (isDispatch)
      dispatch(
        handleMarkers({
          data: res,
          backupParams: { title: isDel ? 'Delete Markers' : 'Add Markers' },
        })
      );
    return res;
  };

  const addMarkers = (markersToAdd: MarkerId[], params?: Params) => {
    const data = parseParams(params);
    const { markers, storyBoard, duration, isErr } = data;
    if (!markers || !duration) return;
    const res = markersApi(markers, storyBoard).add(markersToAdd);
    if (isErr) {
      const wsTime = markersToAdd[0];
      const cell = getCursorCell(wsTime, markers);
      if (isSmallRegion(wsTime, cell, markers, duration)) {
        handleError('smallRegion');
        return;
      }
    }
    return handleLinks(res, data);
  };

  const delMarkers = (params?: Params) => {
    const data = parseParams(params);
    const { markers, storyBoard, selectedMarkers, clipsMap, isErr } = data;
    if (!markers || !clipsMap) return;
    if (selectedMarkers.length === 0) return { markers, storyBoard };
    const res = markersApi(markers, storyBoard).delete(selectedMarkers, clipsMap);

    if (!res) {
      if (isErr) handleError('notEnoughCells');
      return;
    }

    const replacedClips: ClipId[] = selectedMarkers.flatMap((marker) =>
      getSbTracks().reduce((acc, track) => {
        const clip = storyBoard[track][marker + 1];
        if (clip !== EMPTY_CELL) acc.push(clip);
        return acc;
      }, [] as ClipId[])
    );

    return handleLinks(res, data, replacedClips, true);
  };

  return { addMarkers, delMarkers };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { markers, storyBoard, linkedClips, clipsMap, selectedMarkers } = state.editor;
  const { duration } = state.audio;
  return {
    clipsMap,
    storyBoard,
    linkedClips,
    selectedMarkers,
    duration,
    markers,
  };
};
